// Browser + profile bundle sync for one worker.
//
// This replaces an "Auto-update" card that reported cron state. In a container
// that reporter could only ever answer "not installed" — the image has no cron,
// runs as a non-root user and mounts a read-only root filesystem — so the card
// permanently told operators to "re-run setup.sh" for a script that does not
// exist there, and its enable/disable buttons could not succeed.
//
// What is real is the sync itself: bundles are downloaded and verified into the
// install volume without restarting anything. Updating the worker IMAGE is a
// different axis and lives under Infrastructure > Updates.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { formatRelative } from "@veil/ui/lib/format";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import RefreshCw from "lucide-react/dist/esm/icons/refresh-cw";
import { toast } from "sonner";
import { Link, useLocation } from "wouter";

export function BundleSyncCard({
	hostname,
	lastSyncAt,
	disabled,
}: {
	hostname: string;
	/** Last completed sync, or null when none has run on this worker. */
	lastSyncAt: string | null;
	disabled?: boolean;
}) {
	const queryClient = useQueryClient();
	const [, navigate] = useLocation();

	const syncNow = useMutation({
		mutationFn: async () => {
			const res = await fetch(
				`/api/workers/${encodeURIComponent(hostname)}/sync`,
				{ method: "POST", credentials: "include" },
			);
			const data = await res.json();
			if (!res.ok || !data.success) {
				throw new Error(data?.error?.message ?? `Sync failed (${res.status})`);
			}
			return data as { operationId?: string };
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["workers", hostname] });
			if (data.operationId) {
				navigate(`/operations/${data.operationId}`);
				return;
			}
			toast.success("Bundle sync started");
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Sync failed"),
	});

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
				<CardTitle className="flex items-center gap-2 text-base">
					<RefreshCw className="h-4 w-4" /> Browser &amp; profile bundles
				</CardTitle>
				<Button
					variant="secondary"
					size="sm"
					disabled={disabled || syncNow.isPending}
					onClick={() => syncNow.mutate()}
				>
					{syncNow.isPending ? (
						<Loader2 className="animate-spin" />
					) : (
						<RefreshCw />
					)}
					{syncNow.isPending ? "Starting…" : "Sync now"}
				</Button>
			</CardHeader>
			<CardContent className="text-sm text-muted-foreground">
				<p>
					{lastSyncAt ? (
						<>
							Last synced{" "}
							<span className="font-mono text-xs tabular-nums">
								{formatRelative(lastSyncAt)}
							</span>
						</>
					) : (
						"No sync has run on this worker yet."
					)}
				</p>
				<p className="mt-1 text-xs">
					Downloads and verifies browser builds and profile bundles into this
					worker's volume; the container keeps running. To update the worker
					image itself, use <Link href="/updates">Updates</Link>.
				</p>
			</CardContent>
		</Card>
	);
}
